<template>
    <PageWrapper>
        <template #headline>
            <div class="flex h-auto w-full items-center justify-between">
                <div class="flex items-center justify-center space-x-2">
                    <fa icon="fa-money-bill" />
                    <span>{{ $t('tax') }} {{ tax?.name }}</span>
                </div>
                <div class="flex items-center justify-center space-x-2">
                    <EditButton
                        v-if="userStore.can('update_taxes')"
                        :title="$t('edit')"
                        @click="editTaxModalOpen = true"
                    />
                </div>
            </div>
        </template>
        <EditTaxModal
            v-if="editTaxModalOpen"
            v-model:open="editTaxModalOpen"
            :tax="!tax ? {} as TaxData : tax"
            @cancel="editTaxModalOpen = false"
            @submit="editTax"
        />
        <TaxSummary />
        <div
            class="flex h-auto w-full flex-col items-start justify-start space-y-2"
        >
            <p class="text-sm font-bold">{{ $t('tax_details') }}</p>
            <div
                class="grid h-10 w-full grid-cols-3 items-center rounded-md bg-white px-4"
            >
                <p class="text-sm font-medium">{{ $t('name') }}</p>
                <p class="text-sm font-medium">{{ $t('rate') }}</p>
                <p class="text-sm font-medium">{{ $t('amount') }}</p>
            </div>
            <div
                v-for="detail in tax?.tax_details"
                :key="detail.name"
                class="grid h-10 w-full grid-cols-3 items-center rounded-md bg-white px-4 duration-150 hover:bg-slate-50"
            >
                <p class="col-span-1 text-sm font-medium">{{ detail.name }}</p>
                <p class="col-span-1 text-sm font-medium">{{ detail.rate }}</p>
                <p class="col-span-1 text-sm font-medium">
                    {{ detail.amount }}
                </p>
            </div>
        </div>
        <AuditsTable
            v-if="tax?.tax_uid"
            :initial-filters="{
                auditable_id: tax?.tax_uid,
                auditable_type: auditedModels.tax.value,
            }"
        />
    </PageWrapper>
</template>

<script setup lang="ts">
import { useSummary } from '@src/composables/common'
import EditButton from '@src/partials/actions/EditButton.vue'
import AuditsTable from '@src/partials/audits/AuditsTable.vue'
import PageWrapper from '@src/partials/pages/PageWrapper.vue'
import EditTaxModal from '@src/partials/taxes/EditTaxModal.vue'
import { SummaryItem } from '@src/types/Summary'
import { TaxData } from '@src/types/Tax'
import { getTax, updateTax } from '@src/utils/api/taxes'
import { useAuditsStore } from '@src/utils/stores/audits'
import { useUserStore } from '@src/utils/stores/user'
import moment from 'moment'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const route = useRoute()
const taxId = route.params.id
const i18n = useI18n()
const userStore = useUserStore()
const auditsStore = useAuditsStore()
const { auditedModels } = storeToRefs(auditsStore)

const editTaxModalOpen = ref(false)
const tax = ref<TaxData>()

const taxOverviewItems = computed<SummaryItem[]>(() => {
    return [
        {
            label: 'ID',
            value: tax.value?.tax_uid,
            copy: true,
        },
        {
            label: i18n.t('tax_name'),
            value: tax.value?.name,
        },
        {
            label: i18n.t('tax_formula_rate'),
            value: tax.value?.formula_rate,
        },
        {
            label: i18n.t('tax_formula_amount'),
            value: tax.value?.formula_amount,
        },
        {
            label: i18n.t('target'),
            value:
                tax.value?.target === 'natural'
                    ? i18n.t('natural_profile')
                    : i18n.t('legal_profile'),
        },
        {
            label: i18n.t('issuer_country'),
            value: tax.value?.issuer_country,
        },
        {
            label: i18n.t('investor_country'),
            value: tax.value?.investor_country,
        },
        {
            label: i18n.t('asset_types'),
            value:
                tax.value?.asset_types && tax.value?.asset_types.length > 0
                    ? tax.value?.asset_types.join(', ')
                    : '-',
        },
        {
            label: i18n.t('platforms'),
            value:
                tax.value?.platform_names &&
                tax.value?.platform_names.length > 0
                    ? tax.value?.platform_names.join(', ')
                    : '-',
        },
        {
            label: i18n.t('updated_at'),
            value: moment(tax.value?.updated_at).format('DD.MM.yyyy HH:mm'),
        },
        {
            label: i18n.t('created_at'),
            value: moment(tax.value?.created_at).format('DD.MM.yyyy HH:mm'),
        },
    ]
})

const { Summary: TaxSummary } = useSummary(taxOverviewItems, {
    title: 'summary',
})

const editTax = async (form: TaxData) => {
    const updateTaxRequest = await updateTax(tax.value?.tax_uid as string, form)
    if (updateTaxRequest.status === 200) {
        editTaxModalOpen.value = false
        tax.value = updateTaxRequest.data.tax
    }
}

onMounted(() => {
    getTax(taxId as string).then((res) => {
        tax.value = res.data.tax
        tax.value['asset_types'] = tax.value.asset_types ?? []
    })
})
</script>
